import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import PublicLayout from "@/layouts/PublicLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const MAX_POLLS = 10;

export default function PaymentSuccessPage() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("polling");
  const [payment, setPayment] = useState(null);
  const [polls, setPolls] = useState(0);
  useDocumentTitle("Payment successful");

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPayment(data);
        if (data.status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "failed" || data.status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (err) {
        // keep polling
      }
      attempts += 1;
      setPolls(attempts);
      if (attempts >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  const nextHref = payment?.booking_id ? `/dashboard/bookings/${payment.booking_id}`
    : payment?.order_id ? `/dashboard/orders/${payment.order_id}`
    : payment?.featured_placement_id ? "/dashboard/featured"
    : "/dashboard";

  return (
    <PublicLayout>
      <div className="mx-auto max-w-lg px-6 py-24" data-testid="payment-success-page">
        {status === "polling" && (
          <div className="text-center">
            <Loader2 className="h-10 w-10 mx-auto text-[#165a71] animate-spin" />
            <div className="mt-6 font-display text-2xl font-semibold text-[#071b22]">Confirming your payment…</div>
            <div className="text-sm text-[#252A34]/70 mt-1">Hold tight — this usually takes a couple of seconds. ({polls}/{MAX_POLLS})</div>
          </div>
        )}
        {status === "paid" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <div className="mt-5 font-display text-3xl font-semibold text-[#071b22]" data-testid="payment-confirmed">Payment confirmed</div>
            <div className="text-sm text-[#252A34]/70 mt-2">
              Your Gulf Coast Work transaction is secured. Provider payout is released according to the service completion policy.
            </div>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 text-sm inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#165a71]" />
              <span className="text-[#252A34]/80">Receipt saved to your dashboard.</span>
            </div>
            <div className="mt-8">
              <Link to={nextHref} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-6 py-3 font-medium hover:bg-[#11485a]" data-testid="payment-continue">
                Continue <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
        {status === "failed" && (
          <div className="text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">✕</div>
            <div className="mt-5 font-display text-2xl font-semibold text-[#071b22]">Payment did not complete</div>
            <div className="text-sm text-[#252A34]/70 mt-1">You haven't been charged. Try again or use another card.</div>
            <Link to={nextHref} className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-medium">Back to workspace</Link>
          </div>
        )}
        {status === "timeout" && (
          <div className="text-center">
            <div className="font-display text-2xl font-semibold text-[#071b22]">Still processing…</div>
            <div className="text-sm text-[#252A34]/70 mt-1">Your workspace will update as soon as the payment is confirmed.</div>
            <Link to={nextHref} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium">Go to workspace</Link>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
